import { useEffect, useState } from 'react';

import { orderService } from '@services/orderService';
import type { Order } from '@services/orderTypes';

interface OrderState {
  orders: Order[];
  fills: any[];
}

/**
 * Hook for managing open orders state
 * Subscribes to OrderService for real-time updates
 */
export function useOrders() {
  const [state, setState] = useState<OrderState>(() => orderService.getState());

  useEffect(() => {
    const unsubscribe = orderService.subscribe(setState);
    return () => {
      unsubscribe();
    };
  }, []);

  return {
    openOrders: state.orders,
    orderHistory: state.fills,
    // Convenience methods
    getOrderById: (id: string) => orderService.getOrderById(id),
    cancelOrder: (id: string) => orderService.cancelOrder(id),
    editOrder: (id: string, params: any) => orderService.editOrder(id, params),
  };
}

/**
 * Hook for order history with pagination support
 */
export function useOrderHistory() {
  const [history, setHistory] = useState(() => orderService.getOrderHistory());

  useEffect(() => {
    // Order history doesn't change frequently, so we can just fetch it once
    // In the future, we might want to add real-time updates here too
    setHistory(orderService.getOrderHistory());
  }, []);

  return {
    orders: history.orders,
    fills: history.fills,
    // Helper methods
    getOrdersForAsset: (asset: string) =>
      history.orders.filter(order => order.asset === asset),
    getFillsForOrder: (orderId: string) =>
      history.fills.filter(fill => fill.orderId === orderId),
  };
}
